import { fg } from '@atlaskit/platform-feature-flags';
import { getSupportedAccessibleProducts as getSupportedAccessibleProductsFromRovoConfig } from '@atlassian/rovo-connectors-config/utils/get-accessible-products';

import { ThirdPartyConfigs } from '../constants/3p-product-configs';
import { ProductKeys } from '../constants/products';
import { isForgeConnectorProductKey } from '../constants/schemas/3p-config';

/**
 * Utility to get the accessible products based on the lifecycle of fetching
 * the configuration of what a user is authorised to use.
 *
 * NOTE: The `products` parameter is deprecated as we are removing it as a
 * prop from the root <SearchPage/>
 *
 * @param supportedProducts Record of products supported by FPS
 * @param products Overrides the list of products a user can access
 * @param accessibleProducts The list of products a user is authorized to use. Initially
 * undefined while the user configuration is being retrieved.
 */
export const getAccessibleProducts: ({
	supportedProducts,
	products,
	accessibleProducts,
}: {
	supportedProducts: ProductKeys[];

	/**
	 * DEPRECATED - Do not use. There shouldn't be any usages of this and can be cleaned up.
	 */
	products?: ProductKeys[];
	accessibleProducts?: ProductKeys[];
}) => ProductKeys[] = ({
	supportedProducts,
	products,
	accessibleProducts,
}: {
	supportedProducts: ProductKeys[];
	products?: ProductKeys[];
	accessibleProducts?: ProductKeys[];
}): ProductKeys[] => {
	// Facade: when rovo_fps_use_rovo_config is enabled, delegate to the
	// canonical implementation in @atlassian/rovo-connectors-config so that
	// search-page and other consumers share a single source of truth.
	// When disabled, fall through to the inline legacy implementation below.
	if (fg('rovo_fps_use_rovo_config')) {
		return getSupportedAccessibleProductsFromRovoConfig({
			supportedProducts,
			products,
			accessibleProducts,
		});
	}

	if (!accessibleProducts) {
		return [];
	}

	const fgControlledProducts: Partial<Record<ProductKeys, boolean>> = {
		[ProductKeys.Talent]: fg('talent_global_search'),
	};

	const disabledProducts3PFromConfig: ProductKeys[] = [];
	ThirdPartyConfigs.forEach((config, productKey3P) => {
		if (config && !config.isEnabled()) {
			disabledProducts3PFromConfig.push(productKey3P);
		}
	});

	const accessibleAndSupportedProducts = accessibleProducts
		.filter(
			(productKey) =>
				!disabledProducts3PFromConfig.find(
					(featureGatedProduct) => featureGatedProduct === productKey,
				) && (productKey in fgControlledProducts ? fgControlledProducts[productKey] : true),
		)
		.filter((productKey) => {
			// Forge connectors are dynamically added and won't be in supportedProducts
			if (isForgeConnectorProductKey(productKey) && fg('rovo-search-dynamic-forge-config')) {
				return true;
			}
			// For hardcoded products, check if they're in supportedProducts
			return supportedProducts.includes(productKey as ProductKeys);
		});

	if (products) {
		const allowedProducts = products.filter((product) =>
			accessibleAndSupportedProducts.includes(product),
		);

		return allowedProducts;
	} else {
		if (
			//TODO: move this logic to xpsearch aggregator before release with the correct conditions
			// eslint-disable-next-line @atlaskit/platform/no-preconditioning
			accessibleAndSupportedProducts.includes(ProductKeys.JiraServiceManagement) &&
			fg('assets_rovo_search_all') && // add assets as a product if JSM license is present, but this is until assets becomes a platform
			!fg('aurum-assets-as-a-platform') // disable this when assets becomes a platform and we will start getting assets as its product
		) {
			return [...accessibleAndSupportedProducts, ProductKeys.Assets];
		}
		return accessibleAndSupportedProducts;
	}
};
